/**
 * @fileoverview 스포츠 데이터 관리를 위한 커스텀 훅
 * @description 종목 목록 조회, 종목명 변환, 종목 옵션 생성 등의 기능을 제공
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSports } from '../api';
import { Sport, SportOption, sportsToOptions, getSportLabel, getSportName } from '../types/sports';

/**
 * useSports 훅의 반환 타입 정의
 * @interface UseSportsReturn
 */
interface UseSportsReturn {
  /** 전체 스포츠 목록 */
  sports: Sport[];
  /** 셀렉트박스용 스포츠 옵션 목록 */
  sportOptions: SportOption[];
  /** 데이터 로딩 상태 */
  loading: boolean;
  /** 에러 객체 */
  error: Error | null;
  /** 스포츠 ID로 한글명 조회 */
  getSportLabelById: (sportId: number) => string;
  /** 영문 종목명으로 한글명 조회 */
  getSportLabelByName: (sportName: string) => string;
  /** 한글 종목명으로 영문명 조회 */
  getSportNameByLabel: (sportNameKr: string) => string;
  /** 데이터 새로고침 함수 */
  refetch: () => Promise<void>;
}

/** API 실패 시 사용할 기본 옵션 목록 */
const FALLBACK_OPTIONS: SportOption[] = [
  { value: 'soccer', label: '축구' },
  { value: 'baseball', label: '야구' },
  { value: 'basketball', label: '농구' },
  { value: 'volleyball', label: '배구' },
  { value: 'hockey', label: '하키' }
];

/** API 실패 시 사용할 기본 스포츠 목록 */
const FALLBACK_SPORTS: Sport[] = [
  { sport_id: 1, sport_name: 'soccer', sport_name_kr: '축구' },
  { sport_id: 2, sport_name: 'baseball', sport_name_kr: '야구' },
  { sport_id: 3, sport_name: 'basketball', sport_name_kr: '농구' },
  { sport_id: 4, sport_name: 'volleyball', sport_name_kr: '배구' },
  { sport_id: 5, sport_name: 'hockey', sport_name_kr: '하키' }
];

/**
 * 스포츠 데이터를 관리하는 커스텀 훅
 *
 * @description
 * - 컴포넌트 마운트 시 자동으로 스포츠 목록을 조회
 * - API 실패 시 기본 스포츠 목록으로 폴백
 * - 종목 ID/이름 간 변환 유틸리티 함수 제공
 *
 * @example
 * ```tsx
 * const { sports, sportOptions, getSportLabelByName, loading } = useSports();
 *
 * // 셀렉트박스에서 사용
 * <select>
 *   {sportOptions.map(opt => (
 *     <option key={opt.value} value={opt.value}>{opt.label}</option>
 *   ))}
 * </select>
 *
 * // 영문명을 한글명으로 변환
 * const koreanName = getSportLabelByName('soccer'); // '축구'
 * ```
 *
 * @returns {UseSportsReturn} 스포츠 관련 상태와 유틸리티 함수들
 */
export const useSports = (): UseSportsReturn => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadSports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSports();
      setSports(response.data);
    } catch (err) {
      console.error('스포츠 목록 조회 실패:', err);
      setError(err instanceof Error ? err : new Error('스포츠 목록 조회 실패'));
      setSports(FALLBACK_SPORTS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSports();
  }, [loadSports]);

  const sportOptions = useMemo(() => {
    if (sports.length > 0) {
      return sportsToOptions(sports);
    }
    return FALLBACK_OPTIONS;
  }, [sports]);

  const getSportLabelById = useCallback((sportId: number): string => {
    const sport = sports.find(s => s.sport_id === sportId);
    return sport?.sport_name_kr || String(sportId);
  }, [sports]);

  const getSportLabelByName = useCallback((sportName: string): string => {
    return getSportLabel(sportName, sports);
  }, [sports]);

  const getSportNameByLabel = useCallback((sportNameKr: string): string => {
    return getSportName(sportNameKr, sports);
  }, [sports]);

  return {
    sports,
    sportOptions,
    loading,
    error,
    getSportLabelById,
    getSportLabelByName,
    getSportNameByLabel,
    refetch: loadSports
  };
};

export default useSports;
